import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown, Building2 } from "lucide-react";
import WaitlistForm from "@/components/landing/WaitlistForm";

const EASE = [0.16, 1, 0.3, 1];

const lines = [
  { text: "Hiring,", italic: false },
  { text: "Reimagined for", italic: true },
  { text: "the Ambitious.", italic: false },
];

export default function Hero() {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[96vh] flex flex-col justify-center overflow-hidden pt-32 pb-16">
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
            data-testid="hero-badge"
            className="inline-flex items-center gap-2.5 rounded-full border border-ink/10 bg-white/50 px-4 py-1.5 mb-8"
          >
            <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-bronze" />
            <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-bronze">Coming Soon</span>
          </motion.div>

          <h1 data-testid="hero-headline" className="font-serif font-medium tracking-tight leading-[1.02] text-[13vw] sm:text-6xl lg:text-7xl xl:text-[5.2rem]">
            {lines.map((l, i) => (
              <span key={l.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${l.italic ? "italic text-bronze" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1, delay: 0.45 + i * 0.14, ease: EASE }}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            data-testid="hero-subtext"
            className="mt-7 max-w-md text-base sm:text-lg font-light leading-relaxed text-ink-soft"
          >
            Vacansee is an upcoming hiring platform matching exceptional people
            with hand-vetted vacancies — salary-transparent, human-first, zero
            noise. Launching soon.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="mt-10 flex flex-col gap-5"
          >
            <WaitlistForm idPrefix="hero" />
            <a
              href="#manifesto"
              onClick={(e) => {
                e.preventDefault();
                if (lenis) lenis.scrollTo("#manifesto", { offset: -60, duration: 1.5 });
              }}
              data-testid="hero-manifesto-button"
              className="link-underline self-start text-sm font-medium text-ink inline-flex items-center gap-2"
            >
              Explore Manifesto <ArrowDown className="h-4 w-4" />
            </a>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.3, delay: 0.65, ease: EASE }}
            className="relative overflow-hidden rounded-t-[10rem] rounded-b-3xl border border-ink/10 shadow-[0_30px_80px_rgba(13,27,42,0.18)]"
          >
            <motion.img
              style={{ y: imgY }}
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80"
              alt="A team collaborating in a bright modern studio"
              data-testid="hero-image"
              className="h-[52vh] lg:h-[62vh] w-full object-cover scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/35 via-transparent to-transparent" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.35, ease: EASE }}
            data-testid="hero-float-badge"
            className="absolute -bottom-6 -left-4 sm:-left-10 flex items-center gap-3 rounded-2xl border border-white/60 bg-sand/85 backdrop-blur-xl px-5 py-4 shadow-[0_16px_40px_rgba(13,27,42,0.14)]"
          >
            <Building2 className="h-5 w-5 text-bronze" />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Launch Cohort</p>
              <p className="font-serif text-lg font-semibold leading-tight">50 Vetted Companies</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
